import { useState, useEffect, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/hooks/useConfirm";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import NewListingDialog from "@/components/pharos/NewListingDialog";
import InterestDialog from "@/components/pharos/InterestDialog";
import TermsDialog from "@/components/pharos/TermsDialog";
import DealCard from "@/components/pharos/DealCard";
import { INK, CREAM, GRAY } from "@/components/pharos/art";
import { ASSET_CLASSES, REGIONS, STATUS_LABELS } from "@/components/pharos/constants";
import { BRAND } from "@/lib/constants";
import api, { getErrorMessage } from "@/lib/api";

const ALL_CLASS = "all";
const ALL_REGIONS = "__all__";

const STATUS_TINT = {
  pending_review: "bg-amber-50 text-amber-700 border-amber-100",
  published: "bg-green-50 text-green-700 border-green-100",
  closed: "bg-gray-100 text-gray-600 border-gray-200",
  withdrawn: "bg-gray-100 text-gray-500 border-gray-200",
  rejected: "bg-red-50 text-red-700 border-red-100",
};

function Chip({ tint = "bg-gray-100 text-gray-600 border-gray-200", children }) {
  return (
    <span className={"inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold " + tint}>
      {children}
    </span>
  );
}

function ClassChip({ active, onClick, children }) {
  const style = active
    ? { backgroundColor: INK, borderColor: INK, color: CREAM }
    : { "--pharos-chip-hover": GRAY };
  return (
    <button
      type="button"
      onClick={onClick}
      style={style}
      className={
        "px-3 py-1.5 rounded-full text-xs font-medium border transition-colors " +
        (active ? "" : "bg-white border-gray-200 text-gray-600 hover:border-[var(--pharos-chip-hover)]")
      }
    >
      {children}
    </button>
  );
}

// Owner controls for one of my listings: status chip plus, depending on
// status, the amount_closed / close_date update, Withdraw and Close.
function MyListingFooter({ deal, onUpdate, onWithdraw, onClose, onEdit }) {
  const [closed, setClosed] = useState(deal.amount_closed ?? "");
  const [closeDate, setCloseDate] = useState(deal.close_date || "");
  const published = deal.status === "published";
  const republish = ["withdrawn", "rejected", "closed"].includes(deal.status);
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-1.5">
        <Chip tint={STATUS_TINT[deal.status]}>{STATUS_LABELS[deal.status] || ""}</Chip>
        {deal.pending_revision && <Chip tint="bg-amber-50 text-amber-700 border-amber-100">Edit pending review</Chip>}
      </div>
      <Button size="sm" variant="outline" onClick={() => onEdit(deal)}>
        {republish ? "Edit & republish" : "Edit"}
      </Button>
      {published && (
        <div className="flex flex-wrap items-end gap-2">
          <label className="text-xs text-gray-600">
            Amount closed
            <input
              type="number"
              step="any"
              min="0"
              className="mt-0.5 block w-32 rounded-md border px-2 py-1 text-sm"
              value={closed}
              onChange={(e) => setClosed(e.target.value)}
            />
          </label>
          <label className="text-xs text-gray-600">
            Close date
            <input
              type="date"
              className="mt-0.5 block rounded-md border px-2 py-1 text-sm"
              value={closeDate}
              onChange={(e) => setCloseDate(e.target.value)}
            />
          </label>
          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              onUpdate(deal, {
                ...(closed !== "" ? { amount_closed: Number(closed) } : {}),
                ...(closeDate ? { close_date: closeDate } : {}),
              })
            }
          >
            Save
          </Button>
        </div>
      )}
      {(published || deal.status === "pending_review") && (
        <div className="flex gap-1">
          <Button size="sm" variant="ghost" onClick={() => onWithdraw(deal)}>
            Withdraw
          </Button>
          {published && (
            <Button size="sm" variant="ghost" onClick={() => onClose(deal)}>
              Close
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

export default function PharosPage() {
  const confirm = useConfirm();

  // null while loading, then "full" | "seller_only" | "denied".
  const [access, setAccess] = useState(null);
  const [listings, setListings] = useState([]);
  const [listingsLoading, setListingsLoading] = useState(true);
  const [myListings, setMyListings] = useState([]);
  const [myLoading, setMyLoading] = useState(true);
  const [interests, setInterests] = useState([]);
  const [interestsLoading, setInterestsLoading] = useState(true);

  // Optimistic true; corrected the moment /pharos/terms resolves.
  const [termsAccepted, setTermsAccepted] = useState(true);
  const [termsOpen, setTermsOpen] = useState(false);

  const [newListingOpen, setNewListingOpen] = useState(false);
  const [editDeal, setEditDeal] = useState(null);
  const [detail, setDetail] = useState(null);
  const [interestDeal, setInterestDeal] = useState(null);

  const [selectedClass, setSelectedClass] = useState(ALL_CLASS);
  const [selectedRegion, setSelectedRegion] = useState(ALL_REGIONS);

  const loadListings = useCallback(async () => {
    setListingsLoading(true);
    try {
      const res = await api.get("/pharos/listings");
      setListings(Array.isArray(res.data?.listings) ? res.data.listings : []);
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to load listings"));
      setListings([]);
    } finally {
      setListingsLoading(false);
    }
  }, []);

  const loadMine = useCallback(async () => {
    setMyLoading(true);
    try {
      const res = await api.get("/pharos/listings/mine");
      setMyListings(Array.isArray(res.data?.listings) ? res.data.listings : Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to load your listings"));
      setMyListings([]);
    } finally {
      setMyLoading(false);
    }
  }, []);

  const loadInterests = useCallback(async () => {
    setInterestsLoading(true);
    try {
      const res = await api.get("/pharos/interests");
      setInterests(Array.isArray(res.data?.interests) ? res.data.interests : Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to load your requests"));
      setInterests([]);
    } finally {
      setInterestsLoading(false);
    }
  }, []);

  const loadTerms = useCallback(async () => {
    try {
      const res = await api.get("/pharos/terms");
      setTermsAccepted(!!res.data?.accepted);
    } catch {
      // best-effort: the gated actions still enforce the real check
    }
  }, []);

  // Access first: it decides which tabs exist and which lists are fetched.
  useEffect(() => {
    let cancelled = false;
    api
      .get("/pharos/access")
      .then((res) => {
        if (cancelled) return;
        const a = res.data?.access === "full" ? "full" : res.data?.access === "seller_only" ? "seller_only" : "denied";
        setAccess(a);
        if (a === "full") {
          loadListings();
          loadInterests();
        }
        if (a !== "denied") {
          loadMine();
          loadTerms();
        }
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 403) setAccess("denied");
        else {
          toast.error(getErrorMessage(err, "Failed to load Pharos"));
          setAccess("denied");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [loadListings, loadMine, loadInterests, loadTerms]);

  async function handleUpdate(deal, patch) {
    try {
      await api.patch(`/pharos/listings/${deal.id}`, patch);
      toast.success("Listing updated");
      loadMine();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to update listing"));
    }
  }

  async function handleStatusChange(deal, action, copy) {
    const ok = await confirm({ ...copy, destructive: true });
    if (!ok) return;
    try {
      await api.post(`/pharos/listings/${deal.id}/${action}`);
      toast.success(copy.done);
      loadMine();
      if (access === "full") loadListings();
    } catch (err) {
      toast.error(getErrorMessage(err, copy.fail));
    }
  }

  const handleWithdraw = (deal) =>
    handleStatusChange(deal, "withdraw", {
      title: "Withdraw listing?",
      description: "This listing will no longer be visible.",
      confirmText: "Withdraw",
      done: "Listing withdrawn",
      fail: "Failed to withdraw listing",
    });
  const handleClose = (deal) =>
    handleStatusChange(deal, "close", {
      title: "Close listing?",
      description: "This listing will be marked as closed.",
      confirmText: "Close listing",
      done: "Listing closed",
      fail: "Failed to close listing",
    });

  function handleEdit(deal) {
    if (!termsAccepted) {
      setTermsOpen(true);
      return;
    }
    const { pending_revision: pending, ...rest } = deal;
    setEditDeal(pending ? { ...rest, ...pending } : rest);
  }

  function handleNewListingClick() {
    if (termsAccepted) setNewListingOpen(true);
    else setTermsOpen(true);
  }

  if (access === "denied") return <Navigate to="/documents" replace />;
  if (access === null) return <div className="text-sm text-gray-500">Loading...</div>;

  const full = access === "full";

  const baseFiltered =
    selectedRegion === ALL_REGIONS ? listings : listings.filter((l) => l.geography === selectedRegion);
  const classCounts = { [ALL_CLASS]: baseFiltered.length };
  for (const c of ASSET_CLASSES) classCounts[c.value] = baseFiltered.filter((l) => l.asset_class === c.value).length;
  const filtered = selectedClass === ALL_CLASS ? baseFiltered : baseFiltered.filter((l) => l.asset_class === selectedClass);

  function browseFooter(deal, inDetail) {
    return (
      <div className="flex items-center justify-between gap-2">
        {deal.mine ? (
          <Chip>Yours</Chip>
        ) : deal.my_interest ? (
          <Chip tint="bg-green-50 text-green-700 border-green-100">{deal.my_interest}</Chip>
        ) : (
          <Button
            size="sm"
            className="text-white"
            style={{ backgroundColor: BRAND.bg }}
            onClick={() => {
              setDetail(null);
              setInterestDeal(deal);
            }}
          >
            Interested
          </Button>
        )}
        {!inDetail && (
          <Button size="sm" variant="ghost" onClick={() => setDetail(deal)}>
            View details
          </Button>
        )}
      </div>
    );
  }

  const liveDetail = detail ? listings.find((l) => l.id === detail.id) || detail : null;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Pharos</h1>
        <p className="text-sm text-gray-500 mt-1">
          Elementar makes every introduction. Requests are reviewed before anything is shared.
        </p>
      </div>

      <Tabs defaultValue={full ? "browse" : "mine"}>
        <TabsList>
          {full && <TabsTrigger value="browse">Browse</TabsTrigger>}
          <TabsTrigger value="mine">My listings</TabsTrigger>
          {full && <TabsTrigger value="interests">My interests</TabsTrigger>}
        </TabsList>

        {full && (
          <TabsContent value="browse" className="space-y-4">
            {listingsLoading ? (
              <div className="text-sm text-gray-500">Loading listings...</div>
            ) : listings.length === 0 ? (
              <div className="text-sm text-gray-500">No listings yet.</div>
            ) : (
              <>
                <div className="flex flex-wrap items-center gap-2">
                  <ClassChip active={selectedClass === ALL_CLASS} onClick={() => setSelectedClass(ALL_CLASS)}>
                    All ({classCounts[ALL_CLASS]})
                  </ClassChip>
                  {ASSET_CLASSES.map((c) => (
                    <ClassChip key={c.value} active={selectedClass === c.value} onClick={() => setSelectedClass(c.value)}>
                      {c.label} ({classCounts[c.value] || 0})
                    </ClassChip>
                  ))}
                  <select
                    aria-label="Region"
                    className="ml-auto rounded-md border px-2 py-1.5 text-sm"
                    value={selectedRegion}
                    onChange={(e) => setSelectedRegion(e.target.value)}
                  >
                    <option value={ALL_REGIONS}>All regions</option>
                    {REGIONS.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                </div>
                {filtered.length === 0 ? (
                  <div className="text-sm text-gray-500 py-8 text-center">No listings match your filters.</div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filtered.map((l) => (
                      <DealCard key={l.id} deal={l}>
                        {browseFooter(l, false)}
                      </DealCard>
                    ))}
                  </div>
                )}
              </>
            )}
          </TabsContent>
        )}

        <TabsContent value="mine" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={handleNewListingClick} style={{ backgroundColor: BRAND.bg, color: "#fff" }}>
              <Plus className="w-4 h-4 mr-1" /> New listing
            </Button>
          </div>
          {myLoading ? (
            <div className="text-sm text-gray-500">Loading your listings...</div>
          ) : myListings.length === 0 ? (
            <div className="text-sm text-gray-500">
              No listings yet. Click <strong>New listing</strong> to submit one.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {myListings.map((l) => (
                <DealCard key={l.id} deal={l}>
                  <MyListingFooter deal={l} onUpdate={handleUpdate} onWithdraw={handleWithdraw} onClose={handleClose} onEdit={handleEdit} />
                </DealCard>
              ))}
            </div>
          )}
        </TabsContent>

        {full && (
          <TabsContent value="interests" className="space-y-4">
            {interestsLoading ? (
              <div className="text-sm text-gray-500">Loading...</div>
            ) : interests.length === 0 ? (
              <div className="text-sm text-gray-500">You have not requested any introductions yet.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Listing</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {interests.map((row) => (
                    <TableRow key={row.public_id}>
                      <TableCell className="font-medium">{row.title}</TableCell>
                      <TableCell>{row.display_status}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TabsContent>
        )}
      </Tabs>

      <NewListingDialog
        open={newListingOpen}
        onOpenChange={setNewListingOpen}
        onCreated={loadMine}
        termsAccepted={termsAccepted}
        onTermsRequired={() => setTermsOpen(true)}
      />

      <NewListingDialog
        open={!!editDeal}
        onOpenChange={(o) => !o && setEditDeal(null)}
        onCreated={loadMine}
        mode="edit"
        listing={editDeal}
        termsAccepted={termsAccepted}
        onTermsRequired={() => setTermsOpen(true)}
      />

      {full && (
        <>
          <Dialog open={!!liveDetail} onOpenChange={(o) => !o && setDetail(null)}>
            <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto p-0">
              <DialogHeader className="sr-only">
                <DialogTitle>{liveDetail?.title}</DialogTitle>
                <DialogDescription>Deal details</DialogDescription>
              </DialogHeader>
              {liveDetail && (
                <DealCard deal={liveDetail} large>
                  {browseFooter(liveDetail, true)}
                </DealCard>
              )}
            </DialogContent>
          </Dialog>

          <InterestDialog
            deal={interestDeal}
            open={!!interestDeal}
            onOpenChange={(o) => !o && setInterestDeal(null)}
            onRequested={() => {
              loadListings();
              loadInterests();
            }}
            termsAccepted={termsAccepted}
            onTermsRequired={() => setTermsOpen(true)}
          />
        </>
      )}

      <TermsDialog open={termsOpen} onOpenChange={setTermsOpen} onAccepted={() => setTermsAccepted(true)} />
    </div>
  );
}
